import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { toast } from "react-toastify";

import {
  FiMessageSquare,
  FiTrash2,
  FiCheckCircle,
} from "react-icons/fi";

import { UsersApi } from "../../App/api/users.api.js";
import BasicTable from "../../Components/CommonTable/BasicTable/BasicTable.jsx";
import Button from "../../Components/Button.jsx";

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyDialog, setReplyDialog] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
  });

  // ================= FETCH TICKETS =================

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const payload = {
        page: pagination.page,
        limit: pagination.limit,
      };

      const res = await UsersApi.getAllTickets(payload);

      const responseData = res?.data?.data;

      setTickets(responseData?.tickets || []);

      setPagination((prev) => ({
        ...prev,
        totalCount: responseData?.totalCount || 0,
        totalPages: responseData?.totalPages || 0,
      }));
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [pagination.page]);

  // ================= REPLY =================

  const handleReply = async () => {
    try {
      if (!replyMessage.trim()) {
        return toast.error("Reply message is required");
      }

      await UsersApi.replyToTicket(selectedTicket?._id, {
        message: replyMessage,
      });

      toast.success("Reply sent successfully");

      setReplyDialog(false);
      setReplyMessage("");
      setSelectedTicket(null);

      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to send reply");
    }
  };

  // ================= STATUS UPDATE =================

  const handleStatusUpdate = async (ticketId, status) => {
    try {
      await UsersApi.updateTicketStatus(ticketId, {
        status,
      });

      toast.success("Status updated");

      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to update status");
    }
  };

  // ================= DELETE =================

  const handleDelete = async (ticketId) => {
    try {
      await UsersApi.deleteTicket(ticketId);

      toast.success("Ticket deleted");

      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete ticket");
    }
  };

  // ================= STATUS STYLE =================

  const getStatusStyle = (status) => {
    switch (status) {
      case "OPEN":
        return {
          background: "#fef3c7",
          color: "#b45309",
          border: "1px solid #fcd34d",
        };

      case "IN_PROGRESS":
        return {
          background: "#dbeafe",
          color: "#1d4ed8",
          border: "1px solid #93c5fd",
        };

      case "RESOLVED":
        return {
          background: "#dcfce7",
          color: "#15803d",
          border: "1px solid #86efac",
        };

      case "CLOSED":
        return {
          background: "#fee2e2",
          color: "#dc2626",
          border: "1px solid #fca5a5",
        };

      default:
        return {
          background: "#f3f4f6",
          color: "#374151",
          border: "1px solid #d1d5db",
        };
    }
  };

  // ================= TABLE COLUMNS =================

  const columns = [
    {
      field: "ticket_id",
      header: "Ticket ID",
    },

    {
      field: "customer",
      header: "Customer",
      accessor: (rowData) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-800">
            {rowData?.created_by?.first_name}{" "}
            {rowData?.created_by?.last_name}
          </span>

          <span className="text-xs text-gray-500">
            {rowData?.created_by?.email}
          </span>
        </div>
      ),
    },
    {
      field: "userRole",
      header: "User Role",
      accessor: (rowData) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-800">
            {rowData?.created_by?.userRole.name}
          </span>
        </div>
      ),
    },

    {
      field: "problem_type",
      header: "Problem Type",
    },

    {
      field: "subject",
      header: "Subject",
    },

    {
      field: "description",
      header: "Complaint",
      accessor: (rowData) => (
        <div className="max-w-[250px] truncate text-sm text-gray-700">
          {rowData?.description}
        </div>
      ),
    },

    {
      field: "priority",
      header: "Priority",
      accessor: (rowData) => (
        <Tag
          value={rowData?.priority}
          severity={
            rowData?.priority === "URGENT"
              ? "danger"
              : rowData?.priority === "HIGH"
              ? "warning"
              : rowData?.priority === "MEDIUM"
              ? "info"
              : "success"
          }
        />
      ),
    },

    {
      field: "status",
      header: "Status",
      accessor: (rowData) => (
        <Tag
          value={rowData?.status}
          style={{
            ...getStatusStyle(rowData?.status),
            padding: "6px 14px",
            borderRadius: "999px",
            fontWeight: "600",
            fontSize: "12px",
          }}
        />
      ),
    },

    {
      field: "createdAt",
      header: "Created At",
      accessor: (rowData) =>
        new Date(rowData?.createdAt).toLocaleDateString(),
    },

    {
      field: "actions",
      header: "Actions",
      accessor: (rowData) => (
        <div className="flex items-center gap-2">
          {/* Reply */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTicket(rowData);
              setReplyDialog(true);
            }}
            className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all flex items-center justify-center"
          >
            <FiMessageSquare size={16} />
          </button>

          {/* Resolve */}
          {rowData?.status !== "RESOLVED" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStatusUpdate(rowData?._id, "RESOLVED");
              }}
              className="w-9 h-9 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-all flex items-center justify-center"
            >
              <FiCheckCircle size={16} />
            </button>
          )}

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(rowData?._id);
            }}
            className="w-9 h-9 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all flex items-center justify-center"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-5">
      {/* ================= HEADER ================= */}

      <div className="mb-5">
        <h2 className="text-2xl font-bold text-gray-900">
          Ticket Management
        </h2>

        <p className="text-sm text-gray-500 mt-1">
          Manage customer complaints and support tickets
        </p>
      </div>

      {/* ================= TABLE ================= */}

      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <BasicTable
          columns={columns}
          data={tickets}
          paginatorData={pagination}
          loading={loading}
          onPaginatorClick={(pageData) => {
            setPagination((prev) => ({
              ...prev,
              page: pageData?.page + 1,
            }));
          }}
        />
      </div>

      {/* ================= REPLY MODAL ================= */}

      <Dialog
        visible={replyDialog}
        onHide={() => {
          setReplyDialog(false);
          setReplyMessage("");
        }}
        className="rounded-3xl overflow-hidden"
        style={{ width: "700px" }}
        draggable={false}
        resizable={false}
        showHeader={false}
      >
        <div className="bg-white rounded-3xl overflow-hidden">
          {/* HEADER */}

          <div className="bg-red-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <FiMessageSquare size={24} />
              </div>

              <div>
                <h3 className="text-2xl font-bold ">
                  Reply To Ticket
                </h3>

                <p className="text-sm text-blue-100 mt-1">
                  Respond to customer complaint professionally
                </p>
              </div>
            </div>
          </div>

          {/* BODY */}

          <div className="p-6">
            {/* TICKET CARD */}

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-5">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Ticket ID
                  </p>

                  <h4 className="text-lg font-bold text-gray-800">
                    {selectedTicket?.ticket_id}
                  </h4>
                </div>

                <Tag
                  value={selectedTicket?.status}
                  style={{
                    ...getStatusStyle(selectedTicket?.status),
                    padding: "6px 14px",
                    borderRadius: "999px",
                    fontWeight: "600",
                    fontSize: "12px",
                  }}
                />
              </div>

              {/* CUSTOMER */}

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">
                  Customer
                </p>

                <h4 className="font-semibold text-gray-800">
                  {selectedTicket?.created_by?.first_name}{" "}
                  {selectedTicket?.created_by?.last_name}
                </h4>

                <span className="text-sm text-gray-500">
                  {selectedTicket?.created_by?.email}
                </span>
              </div>

              {/* SUBJECT */}

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">
                  Subject
                </p>

                <h4 className="font-semibold text-gray-800">
                  {selectedTicket?.subject}
                </h4>
              </div>

              {/* DESCRIPTION */}

              <div>
                <p className="text-xs text-gray-500 mb-2">
                  Complaint
                </p>

                <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-700 leading-6">
                  {selectedTicket?.description}
                </div>
              </div>
            </div>

            {/* REPLY BOX */}

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Your Reply
              </label>

              <InputTextarea
                rows={6}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Write a professional response to the customer..."
                className="w-full rounded-2xl border border-gray-300 p-4 text-sm focus:ring-2 focus:ring-blue-500"
                style={{
                  resize: "none",
                }}
              />
            </div>

            {/* BUTTONS */}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                text="Cancel"
                outlined
                onClick={() => {
                  setReplyDialog(false);
                  setReplyMessage("");
                }}
              />

              <Button
                text="Send Reply"
                onClick={handleReply}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TicketManagement;